import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Wallet as WalletIcon, Sparkles, Gift, ShoppingBag, ArrowUpRight, ArrowDownLeft,
  Info, ArrowLeft, Loader2, TrendingUp,
} from "lucide-react";
import http from "@/api";
import { useApp } from "@/context";

const TXN_ICONS = {
  earn: { icon: ArrowUpRight, bg: "rgba(21,128,61,0.1)", color: "#15803d" },
  welcome: { icon: Gift, bg: "rgba(0,0,0,0.18)", color: "#9a6f37" },
  redeem: { icon: ArrowDownLeft, bg: "rgba(220,38,38,0.1)", color: "#dc2626" },
  refund: { icon: ArrowUpRight, bg: "rgba(59,130,246,0.1)", color: "#2563eb" },
  adjust: { icon: Info, bg: "rgba(100,116,139,0.12)", color: "#64748b" },
};

function TxnRow({ txn, lang }) {
  const meta = TXN_ICONS[txn.type] || TXN_ICONS.adjust;
  const Icon = meta.icon;
  const desc = lang === "ar" ? txn.description_ar : txn.description_en;
  const isPositive = txn.points > 0;
  const d = new Date(txn.created_at);
  const dateStr = d.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
    day: "2-digit", month: "short", year: "numeric"
  });
  return (
    <div className="flex items-center gap-3 py-3" data-testid={`wallet-txn-${txn.id}`}>
      <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
           style={{ background: meta.bg, color: meta.color }}>
        <Icon size={16} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{desc || txn.type}</div>
        <div className="text-xs opacity-60">{dateStr}</div>
      </div>
      <div className="font-semibold tabular-nums"
           style={{ color: isPositive ? "#15803d" : "#dc2626" }}>
        {isPositive ? "+" : ""}{txn.points}
      </div>
    </div>
  );
}

export default function Wallet() {
  const { user, t, lang, formatPrice } = useApp();
  const [data, setData] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [{ data: w }, { data: c }] = await Promise.all([
          http.get("/wallet/me"),
          http.get("/wallet/config"),
        ]);
        setData(w);
        setConfig(c);
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  if (!user) {
    return (
      <div className="p-8 text-center">
        <WalletIcon size={56} className="mx-auto opacity-30 mb-4" />
        <p className="opacity-70 mb-4">{lang === "ar" ? "سجّل دخولك لعرض المحفظة" : "Log in to view wallet"}</p>
        <Link to="/login" className="btn-primary inline-flex" data-testid="wallet-login-cta">
          {t("login")}
        </Link>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="animate-spin mx-auto" size={28} />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 space-y-5" data-testid="wallet-page">
      {/* Back link */}
      <Link to="/profile" className="inline-flex items-center gap-1.5 text-sm opacity-70 hover:opacity-100">
        <ArrowLeft size={14} className={lang === "ar" ? "rtl-flip" : ""} />
        {lang === "ar" ? "رجوع" : "Back"}
      </Link>

      {/* Balance hero card */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl p-6 relative overflow-hidden"
        style={{
          background: "linear-gradient(135deg, #000000 0%, #1F1F1F 60%, #2a7558 100%)",
          color: "#fff",
          boxShadow: "0 20px 50px rgba(0,0,0,0.35)",
        }}
        data-testid="wallet-balance-card"
      >
        {/* Decorative gold orb */}
        <div
          className="absolute rounded-full opacity-20"
          style={{
            width: 220, height: 220, right: -60, top: -60,
            background: "radial-gradient(circle, #000000 0%, transparent 70%)",
          }}
        />
        <div className="flex items-start justify-between relative z-10">
          <div>
            <div className="text-xs uppercase tracking-widest opacity-80 flex items-center gap-1.5">
              <WalletIcon size={12} /> {t("walletBalance")}
            </div>
            <div className="mt-2 flex items-baseline gap-2">
              <span className="text-5xl font-bold tabular-nums">{data.balance}</span>
              <span className="text-sm opacity-80">{t("points")}</span>
            </div>
            <div className="mt-1 text-sm opacity-80">
              ≈ {formatPrice(data.value_sar)} · {t("walletValue")}
            </div>
          </div>
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
               style={{ background: "rgba(0,0,0,0.25)", border: "1px solid rgba(0,0,0,0.4)" }}>
            <Sparkles size={22} style={{ color: "#000000" }} />
          </div>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3 relative z-10">
          <div className="p-3 rounded-2xl" style={{ background: "rgba(255,255,255,0.08)" }}>
            <div className="text-[10px] uppercase tracking-widest opacity-70">{t("walletEarned")}</div>
            <div className="font-semibold mt-0.5 tabular-nums">+{data.lifetime_earned}</div>
          </div>
          <div className="p-3 rounded-2xl" style={{ background: "rgba(255,255,255,0.08)" }}>
            <div className="text-[10px] uppercase tracking-widest opacity-70">{t("walletRedeemed")}</div>
            <div className="font-semibold mt-0.5 tabular-nums">−{data.lifetime_redeemed}</div>
          </div>
        </div>
      </motion.div>

      {/* How to earn card */}
      <div className="card-soft p-4 flex items-start gap-3"
           style={{ background: "linear-gradient(135deg,#fbf6ec 0%,#fff 100%)" }}
           data-testid="wallet-howto">
        <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
             style={{ background: "var(--secondary)", color: "#000" }}>
          <TrendingUp size={18} />
        </div>
        <div className="flex-1 text-sm">
          <div className="font-semibold">{t("walletHowToEarn")}</div>
          <div className="opacity-70 mt-0.5 text-xs leading-relaxed">
            {t("walletEarnHint")}
            {config && (
              <> · {lang === "ar" ? "أقل استرداد" : "Min redeem"}: <b>{config.min_redeem_points}</b> {t("points")}</>
            )}
          </div>
        </div>
        <Link to="/shop" className="chip !text-xs shrink-0" data-testid="wallet-shop-link">
          <ShoppingBag size={12} />
          {lang === "ar" ? "تسوق" : "Shop"}
        </Link>
      </div>

      {/* Transactions */}
      <div className="card-soft p-5" data-testid="wallet-history-card">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm uppercase tracking-widest opacity-60">
            {t("walletHistory")}
          </div>
          <div className="text-xs opacity-60">{data.transactions.length}</div>
        </div>
        {data.transactions.length === 0 ? (
          <div className="py-8 text-center opacity-60 text-sm">
            <WalletIcon size={36} className="mx-auto mb-2 opacity-40" />
            {t("walletNoTxn")}
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--border)" }}>
            {data.transactions.map((txn) => (
              <TxnRow key={txn.id} txn={txn} lang={lang} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
